/**
 * Быстрый переход в шапке: «Сделка № …». Цифры (до 8) — Enter открывает
 * сделку; телефон — выпадают сделки и лиды из `GET /api/search`, клик
 * открывает. На карточке сделки в поле её номер: фокус выделяет его целиком,
 * можно сразу печатать новый.
 */
import { useQuery } from "@tanstack/react-query";
import { type KeyboardEvent, useEffect, useState } from "react";
import { useLocation, useMatch, useNavigate } from "react-router";

import { loadSearch } from "@/lib/search";

type Hit = { key: string; to: string; title: string; hint: string; active: boolean };

const isDealNo = (s: string) => /^\d{1,8}$/.test(s);
const digits = (s: string) => s.replace(/\D/g, "").length;

export function QuickJump() {
  const dealId = useMatch("/deals/:dealId")?.params.dealId ?? "";
  const { search } = useLocation();
  const navigate = useNavigate();
  const [value, setValue] = useState(dealId);
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);

  useEffect(() => setValue(dealId), [dealId]);

  // Телефон ищем с паузой 300 мс; номер сделки не ищем — его открывают Enter.
  useEffect(() => {
    const s = value.trim();
    const t = setTimeout(() => setTerm(!isDealNo(s) && digits(s) >= 5 ? s : ""), 300);
    return () => clearTimeout(t);
  }, [value]);

  const q = useQuery({ queryKey: ["quick-jump", term], queryFn: () => loadSearch(term), enabled: term !== "" });

  const hits: Hit[] = q.data
    ? [
        ...q.data.deals.map((d) => ({
          key: `d${d.id}`,
          to: `/deals/${d.id}`,
          title: `Сделка ${d.id}${d.title ? ` · ${d.title}` : ""}`,
          hint: [d.stage_name ?? d.stage, d.assigned].filter(Boolean).join(" · "),
          active: d.active,
        })),
        ...q.data.leads.map((l) => ({
          key: `l${l.id}`,
          to: `/leads/${l.id}`,
          title: `Лид ${l.id}${l.title ? ` · ${l.title}` : ""}`,
          hint: [l.status_name, l.assigned].filter(Boolean).join(" · "),
          active: l.active,
        })),
      ]
    : [];

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    const s = value.trim();
    if (e.key === "Escape") {
      setOpen(false);
      setValue(dealId);
    } else if (e.key === "ArrowDown" && hits.length) {
      e.preventDefault();
      setCursor((c) => (c + 1) % hits.length);
    } else if (e.key === "ArrowUp" && hits.length) {
      e.preventDefault();
      setCursor((c) => (c - 1 + hits.length) % hits.length);
    } else if (e.key === "Enter" && s) {
      e.preventDefault();
      // Вкладку карточки (`?tab=bonus`) сохраняем при переходе между сделками.
      if (isDealNo(s)) go(`/deals/${Number(s)}${dealId ? search : ""}`);
      else if (open && hits[cursor]) go(hits[cursor].to);
      else go(`/search?q=${encodeURIComponent(s)}`);
      (e.target as HTMLInputElement).blur();
    }
  };

  const showList = open && term !== "" && value.trim() === term;

  return (
    <div className="relative flex items-center gap-1.5 text-[13.5px]" data-annot="быстрый переход">
      <label htmlFor="quick-jump" className="whitespace-nowrap font-medium text-fg">
        Сделка №
      </label>
      <input
        id="quick-jump"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setOpen(true);
          setCursor(0);
        }}
        onFocus={(e) => {
          e.target.select();
          setOpen(true);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKey}
        autoComplete="off"
        placeholder="номер или телефон"
        className="h-8 w-44 rounded-[var(--radius-ui)] border border-border bg-bg px-2 font-mono text-fg focus:border-accent focus:outline-none"
      />
      {showList && (
        <div className="absolute left-0 top-9 z-40 w-[26rem] max-w-[90vw] overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg shadow-lg">
          {q.isPending ? (
            <div className="px-3 py-2 text-[12.5px] text-fg-muted">Ищу…</div>
          ) : q.isError ? (
            <div className="px-3 py-2 text-[12.5px] text-err">Поиск не удался: {String(q.error.message)}</div>
          ) : hits.length === 0 ? (
            <div className="px-3 py-2 text-[12.5px] text-fg-muted">Ничего не нашлось</div>
          ) : (
            <ul className="max-h-80 overflow-y-auto py-1">
              {hits.map((h, i) => (
                <li key={h.key}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => go(h.to)}
                    onMouseEnter={() => setCursor(i)}
                    className={`flex w-full flex-col px-3 py-1.5 text-left text-[12.5px] ${i === cursor ? "bg-bg-hover" : ""}`}
                  >
                    <span className={h.active ? "text-fg" : "text-fg-muted"}>{h.title}</span>
                    {h.hint && <span className="text-[11.5px] text-fg-faint">{h.hint}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => go(`/search?q=${encodeURIComponent(term)}`)}
            className="w-full border-t border-border px-3 py-1.5 text-left text-[12px] text-accent hover:bg-bg-hover"
          >
            все результаты →
          </button>
        </div>
      )}
    </div>
  );
}
